import { Controller, type Control, type FieldValues, type Path, type RegisterOptions } from 'react-hook-form';
import TextField, { type TextFieldProps } from '@mui/material/TextField';

/**
 * Reusable TextField controlled by react-hook-form's Controller.
 *
 * Example:
 * <FormTextField control={control} name="email" label="Email" rules={{ required: 'Email is required' }} />
 */
type FormTextFieldProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  formatValue?: (value: number | string) => string;
  parseValue?: (inputValue: string) => number | string;
  /** When true, empty input is stored as undefined instead of '' or 0 */
  allowEmpty?: boolean;
} & Omit<TextFieldProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'inputRef'>;

export function FormTextField<TFieldValues extends FieldValues>(props: FormTextFieldProps<TFieldValues>) {
  const {
    name,
    control,
    rules,
    helperText,
    error,
    variant = 'outlined',
    formatValue,
    parseValue,
    allowEmpty,
    onBlur: onBlurProp,
    ...textFieldProps
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const displayValue = formatValue ? formatValue(field.value) : (field.value ?? '');

        return (
          <TextField
            helperText={helperText ?? fieldState.error?.message}
            {...textFieldProps}
            {...field}
            value={displayValue}
            onChange={(e) => {
              const raw = e.target.value;
              if (allowEmpty && raw === '') {
                field.onChange(undefined);
              } else if (parseValue) {
                field.onChange(parseValue(raw));
              } else {
                field.onChange(textFieldProps.type === 'number' ? Number(raw) : raw);
              }
            }}
            onBlur={(e) => {
              // RHF's field.onBlur marks the field as touched; chain the
              // caller's onBlur after it so save-on-blur callers can hook in
              // without losing the touched-state side effect.
              field.onBlur();
              onBlurProp?.(e);
            }}
            variant={variant}
            error={error ?? Boolean(fieldState.error)}
            fullWidth={textFieldProps.fullWidth ?? true}
          />
        );
      }}
    />
  );
}
